/**
 * Dit anvandaren var pa vag nar inloggningen kravdes.
 *
 * Direktlankar i mejl ("har ar avstamningen for projekt X") ska leda hela vagen
 * fram aven om mottagaren inte har en aktiv session. Annars hamnar de pa
 * startsidan efter inloggningen och far leta reda pa sidan sjalva.
 *
 * Bakgrund (verifierat i webblasaren 2026-09-15): malet sparades redan, men
 * skrevs over med "/login" sa fort inloggningssidan gjorde sin EGEN
 * sessionskontroll och fick 401. Inloggningssidan avvisar "/login" som mal och
 * foll darfor tillbaka till startsidan — varje gang, inte bara ibland.
 * Atgarden ligger darfor pa SKRIVNINGEN: en inloggningssida ar aldrig ett mal
 * att atervanda till, sa den far heller aldrig sparas.
 */
import { isPublicRoute } from './isPublicRoute';

export const NYCKEL_POST_LOGIN = 'postLoginRedirect';

// Sidor som aldrig ar ett meningsfullt mal att skickas tillbaka till efter
// inloggning — de ar vagen IN i systemet, inte destinationen.
const EJ_MAL = [
  '/login',
  '/field/login',
  '/accept-invite',
  '/reset-password',
  '/skola',
];

function arEjMal(sokvag) {
  return EJ_MAL.some(
    (p) => sokvag === p || sokvag.startsWith(`${p}/`) || sokvag.startsWith(`${p}?`),
  );
}

/**
 * Ar sokvagen nagot vi bor skicka tillbaka anvandaren till efter inloggning?
 *
 * @param {string} sokvag t.ex. "/bak-avstamning?project=29"
 * @returns {boolean}
 */
export function arGiltigtInloggningsmal(sokvag) {
  if (!sokvag || typeof sokvag !== 'string') return false;
  // Bara interna, absoluta sokvagar. "//example.com" och "/\example.com" tolkas
  // av webblasaren som EXTERNA adresser — de far aldrig bli ett mal, annars
  // gar inloggningen att anvanda som oppen vidarebefordran.
  if (!sokvag.startsWith('/')) return false;
  if (sokvag.startsWith('//') || sokvag.startsWith('/\\')) return false;
  if (arEjMal(sokvag)) return false;
  // Publika vyer kraver ingen inloggning och ar darfor inget mal att spara.
  if (isPublicRoute(sokvag.split('?')[0])) return false;
  return true;
}

/**
 * Spara ett mal infor inloggningen. Ogiltiga mal ignoreras tyst, och ett
 * redan sparat mal lamnas da orort.
 *
 * @param {string} sokvag
 */
export function sparaInloggningsmal(sokvag) {
  try {
    if (utloggningPagar()) return;
    if (!arGiltigtInloggningsmal(sokvag)) return;
    window.sessionStorage.setItem(NYCKEL_POST_LOGIN, sokvag);
  } catch {
    // sessionStorage kan vara blockerat (privat lage, harda cookie-installningar)
  }
}

// Spar under en pagaende, medveten utloggning. Att bara rensa malet racker inte:
// utloggningen nollar sessionen, och de anrop som redan ar i luften (notiser,
// inkorg) svarar da 401 — varpa samma "kom ihag sidan"-logik sparar om sidan en
// brakdel senare. Spärren gor att inget kan sparas fran det att man klickar
// "Logga ut" tills man ar framme pa inloggningssidan.
const NYCKEL_UTLOGGNING = 'kanplan-utloggning-pagar';
// Sakerhetsnat: nar vi av nagon anledning aldrig nar inloggningssidan ska
// sparren slappa av sig sjalv i stallet for att ligga kvar for alltid.
const UTLOGGNING_FONSTER_MS = 30_000;

function utloggningPagar() {
  try {
    const ts = Number(window.sessionStorage.getItem(NYCKEL_UTLOGGNING) || '0');
    if (!Number.isFinite(ts) || ts <= 0) return false;
    if (Date.now() - ts > UTLOGGNING_FONSTER_MS) {
      window.sessionStorage.removeItem(NYCKEL_UTLOGGNING);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Glom ett sparat mal och spara nya under utloggningen.
 *
 * Anropas vid MEDVETEN utloggning: da ska nasta inloggning borja pa startsidan,
 * inte pa sidan den forra anvandaren stod pa. Viktigt pa delade kontorsdatorer,
 * i linje med att utloggningen redan rensar ovrigt anvandarspecifikt state
 * (fynd 2026-08-06).
 */
export function glomInloggningsmal() {
  try {
    window.sessionStorage.removeItem(NYCKEL_POST_LOGIN);
    window.sessionStorage.setItem(NYCKEL_UTLOGGNING, String(Date.now()));
  } catch {
    // ignore
  }
}

/** Spara adressen anvandaren star pa just nu som mal infor inloggningen. */
export function sparaNuvarandeSomInloggningsmal() {
  try {
    const { pathname, search, hash } = window.location;
    // Framme pa inloggningssidan: utloggningen ar genomford och sparren behovs
    // inte langre — nasta tappade session ska kunna spara sitt mal som vanligt.
    if (arEjMal(pathname)) {
      try {
        window.sessionStorage.removeItem(NYCKEL_UTLOGGNING);
      } catch {
        // ignore
      }
    }
    sparaInloggningsmal(`${pathname}${search}${hash}`);
  } catch {
    // ignore
  }
}
